import { z } from "zod";

/**
 * The password policy, in one place.
 *
 * Exported as predicates rather than only as a zod schema because the client
 * needs to tick each rule live as the person types. Two copies of "at least
 * one digit" — one in a regex, one in a checklist — drift, and the way that
 * failure shows up is a form that says every requirement is met and a server
 * that refuses it.
 */
export const PASSWORD_MIN_LENGTH = 8;

export const PASSWORD_RULES = [
  {
    id: "length",
    test: (value: string) => value.length >= PASSWORD_MIN_LENGTH,
  },
  { id: "uppercase", test: (value: string) => /\p{Lu}/u.test(value) },
  { id: "lowercase", test: (value: string) => /\p{Ll}/u.test(value) },
  { id: "digit", test: (value: string) => /\d/.test(value) },
  {
    id: "special",
    // Anything that is not a letter, a digit or whitespace. Defined by
    // exclusion so accented and non-Latin input is not mistaken for a symbol.
    test: (value: string) => /[^\p{L}\p{N}\s]/u.test(value),
  },
] as const;

export type PasswordRuleId = (typeof PASSWORD_RULES)[number]["id"];

/** Which rules a candidate password already satisfies. */
export function checkPassword(value: string): Record<PasswordRuleId, boolean> {
  return Object.fromEntries(
    PASSWORD_RULES.map((rule) => [rule.id, rule.test(value)]),
  ) as Record<PasswordRuleId, boolean>;
}

/**
 * Server-side gate. The message names the failing rule so a non-browser client
 * (Postman, a script) gets something actionable; the dashboard shows the live
 * checklist instead and should never reach these.
 */
export const passwordSchema = PASSWORD_RULES.reduce(
  (schema, rule) => schema.refine(rule.test, { message: `password.${rule.id}` }),
  z.string() as z.ZodType<string>,
);
